import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { FolderKanban, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectSelectModal = ({ open, onOpenChange, onProjectSelect, toolName }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchProjects();
    }
  }, [open, token]);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    const project = projects.find(p => p.id === selectedProject);
    onProjectSelect(project || null);
    onOpenChange(false);
  };

  const handleSkip = () => {
    onProjectSelect(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-violet-600" />
            Select Project
          </DialogTitle>
          <DialogDescription>
            Choose a project to associate with {toolName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {isLoading ? (
            <div className="text-center py-4 text-slate-500">Loading projects...</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-4">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-slate-600">No projects found</p>
              <p className="text-sm text-slate-500 mt-1">Create a project first to associate your work</p>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Project</label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-modal-select">
                  <SelectValue placeholder="Select a project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex flex-col">
                        <span>{p.title}</span>
                        <span className="text-xs text-slate-500">{p.s1000d_version} - {p.capability?.split(' - ')[0]}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1"
              data-testid="skip-project-btn"
            >
              Continue Without Project
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!selectedProject || projects.length === 0}
              className="flex-1 bg-violet-600 hover:bg-violet-700"
              data-testid="select-project-btn"
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSelectModal;
